import {View, Text} from '@react-pdf/renderer';

import {sharedStyles} from './styles/shared-styles';
import {greenColors, orangeColors} from '../../../../../layouts/theme/designSystem';
import {CultureMatchItem} from '../../../../applicant/dashboard/types/ApplicantMatchesTypes';
import {questionCardScalesStyles} from '../../../recruiting/applicant-pdf-export/components/styles/question-card-scales-styles';

interface Props {
  currentLanguage: 'de' | 'en';
  scaleItem: CultureMatchItem;
  isLast: boolean;
}

export const ReportTopQuestionScale = ({currentLanguage, scaleItem, isLast}: Props) => {
  const Bubbles = ({
    companyValue,
    applicantValue,
    scaleConfiguration,
  }: {
    companyValue: number;
    applicantValue: number;
    scaleConfiguration: {minValue: number; maxValue: number};
  }) => {
    let adjustedApplicantValue = applicantValue;

    // Adjust values if they are the same
    if (companyValue === adjustedApplicantValue) {
      adjustedApplicantValue += 0.02;
    }

    const getLinePosition = (value: number) => {
      const totalSteps = scaleConfiguration.maxValue - scaleConfiguration.minValue;
      const percentage = ((value - scaleConfiguration.minValue) / totalSteps) * 100;
      return `${percentage}%`;
    };

    return (
      <View style={questionCardScalesStyles.bubbleContainer}>
        {Array.from({length: scaleConfiguration.maxValue - scaleConfiguration.minValue + 1}, (_, index) => (
          <View key={`${index}-bubble`} style={questionCardScalesStyles.bubble} />
        ))}

        {/* Vertical line for companyValue */}
        <View
          style={[
            questionCardScalesStyles.valueLine,
            {left: getLinePosition(companyValue), backgroundColor: orangeColors[100]},
          ]}
        />

        {/* Vertical line for adjustedApplicantValue */}
        <View
          style={[
            questionCardScalesStyles.valueLine,
            {left: getLinePosition(adjustedApplicantValue), backgroundColor: greenColors[100]},
          ]}
        />
      </View>
    );
  };

  const BackgroundBar = () => <View style={questionCardScalesStyles.backgroundBar} />;

  const renderScaleQuestions = () => {
    return (
      <View
        style={{
          textAlign: 'center',
          width: '245px',
        }}
        wrap={false}
      >
        <View
          key={scaleItem.resourceId}
          style={{
            width: '100%',
          }}
          wrap={false}
        >
          <View
            style={{
              backgroundColor: `${orangeColors[4]}`,
              borderWidth: 1,
              borderColor: `${orangeColors[5]}`,
              padding: 4,
              borderRadius: 4,
              textAlign: 'center',
            }}
          >
            <Text style={sharedStyles.body1}>{scaleItem.title[currentLanguage]}</Text>
            <View style={questionCardScalesStyles.container}>
              <BackgroundBar />
              <Bubbles
                companyValue={scaleItem.companyValue}
                applicantValue={scaleItem.applicantValue}
                scaleConfiguration={scaleItem.scaleConfiguration}
              />
            </View>
            <View style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginTop: '8px'}}>
              <Text
                style={{
                  ...sharedStyles.body1,
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  flexShrink: 1,
                  textAlign: 'left',
                }}
                wrap={false}
              >
                {scaleItem.scaleConfiguration.labelLeft[currentLanguage].charAt(0).toUpperCase() +
                  scaleItem.scaleConfiguration.labelLeft[currentLanguage].slice(1)}
              </Text>
              <Text style={{...sharedStyles.body1, flex: 0.1, textAlign: 'center', color: greenColors[100]}}>
                {'|'}
              </Text>
              <Text
                style={{
                  ...sharedStyles.body1,
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  textAlign: 'right',
                  flexShrink: 1,
                }}
                wrap={false}
              >
                {scaleItem.scaleConfiguration.labelRight[currentLanguage].charAt(0).toUpperCase() +
                  scaleItem.scaleConfiguration.labelRight[currentLanguage].slice(1)}
              </Text>
            </View>
          </View>
        </View>
      </View>
    );
  };

  return (
    <View
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: isLast ? 0 : '6px',
      }}
    >
      {renderScaleQuestions()}
      <Text style={[sharedStyles.h4, {fontWeight: 'bold'}]}>
        {scaleItem.overlap !== undefined ? `${(scaleItem.overlap * 100).toFixed(0)}%` : '-'}
      </Text>
      <View style={{maxWidth: '235px'}}>
        <Text style={sharedStyles.h4}>
          {scaleItem.description ? scaleItem.description[currentLanguage].replace(/\n/g, ' ') : ''}
        </Text>
      </View>
    </View>
  );
};
